import { amplifyClient } from 'api';
import { getUrlToFetchAndAdd, getUrlToUpdateAndRemove } from 'features/profiles/endpoints';
import { createApiSlice } from 'helpers/createApiSlice';
import { parsePaginatedResponse } from 'helpers/pagination';
import { call } from 'redux-saga/effects';
import {
  AnyRecord,
  ApiStateData,
  DataWithPagination,
  MakerSelectors,
  Pagination,
  UserProfile,
} from 'types';

/**
 * FETCH
 */

type DataFetch = DataWithPagination<UserProfile>;

type PayloadFetch = {
  companyId?: string;
};

interface ExtraSelectors extends MakerSelectors<ApiStateData<DataFetch>> {
  getData: (d: ApiStateData<DataFetch>) => Array<UserProfile> | null;
  getPagination: (d: ApiStateData<DataFetch>) => Pagination | undefined;
}

export const profilesFetch = createApiSlice<DataFetch, PayloadFetch | undefined, ExtraSelectors>({
  name: 'profilesFetch',
  *tryGenerator(_, { payload }) {
    const { companyId } = payload || {};

    const { data } = yield call(amplifyClient, {
      method: 'get',
      url: getUrlToFetchAndAdd(companyId),
    });

    return {
      pageObjects: data, // TODO fix the pagination in the back
    };
  },
  extraSelectors: {
    getData: (data) => parsePaginatedResponse(data)?.data,
    getPagination: (data) => parsePaginatedResponse(data)?.pagination,
  },
});

/**
 * ADD
 */

type PayloadAdd = {
  data: Record<string, unknown>;
  companyId?: string;
};

export const profilesAdd = createApiSlice<unknown, PayloadAdd>({
  name: 'profilesAdd',
  *tryGenerator(_, { payload: { data, companyId } }) {
    yield call(amplifyClient, {
      method: 'post',
      url: getUrlToFetchAndAdd(companyId),
      data,
    });

    return null;
  },
});

/**
 * UPDATE
 */

type PayloadUpdate = {
  data: AnyRecord;
  companyId?: string;
};

export const profilesUpdate = createApiSlice<unknown, PayloadUpdate>({
  name: 'profilesUpdate',
  *tryGenerator(_, { payload: { data, companyId } }) {
    const { id, ...remainderData } = data;

    yield call(amplifyClient, {
      method: 'put',
      url: getUrlToUpdateAndRemove(id, companyId),
      data: remainderData,
    });

    return null;
  },
});

/**
 * REMOVE
 */

type PayloadRemove = {
  id: string;
  companyId?: string;
};

export const profilesRemove = createApiSlice<unknown, PayloadRemove>({
  name: 'profilesRemove',
  *tryGenerator(_, { payload: { id, companyId } }) {
    yield call(amplifyClient, {
      method: 'del',
      url: getUrlToUpdateAndRemove(id, companyId),
    });

    return null;
  },
});
